import type { KeyboardEvent, PointerEvent } from 'react'
import { useEffect, useRef } from 'react'

/**
 * A separator that resizes the panel beside it (GH #2973, Joni's spec, approved by Brian). It uses pointer capture, so
 * touch and pen drag too:
 * - drag toward the panel's outside to widen; the width follows the pointer (`onResize`) and is saved on release
 *   (`onCommit`);
 * - Esc during a drag puts the start width back;
 * - the arrow key pointing away from the panel widens it and the other narrows it, by `step`; Home and End go to the
 *   min and the max;
 * - a double-click resets to the default;
 * - a click or tap with no movement switches between the default and the max (WCAG 2.5.7).
 *
 * `useResizableSeparator` gives a separator element all of this, so each keeps its own look: the left sidebar, Perform's
 * notes panel (GH #2975, #2976) and ui-layouts' `ResizableSeparator`, the right sidebar's.
 */

export interface ResizableSeparatorOptions {
  /** The panel's width now, in px unless `unitsPerPx` says otherwise */
  value: number
  min: number
  max: number
  /** The width before any resize, and after a reset */
  defaultValue: number
  /** The accessible name, e.g. "Resize panel" */
  label: string
  /** The id of the panel it resizes */
  controls?: string
  /** Called as the width changes during a drag */
  onResize: (width: number) => void
  /** Called with the width to keep: on release, a key, a reset or a toggle */
  onCommit: (width: number) => void
  /** Called when a drag ends, committed or cancelled, so a live width can be dropped */
  onDragEnd?: () => void
  /** The panel's edge the separator is on. @default 'left': a panel on the right, widened by moving left */
  edge?: 'left' | 'right'
  /** How much an arrow key changes the width. @default 16 (px) */
  step?: number
  /** Value units per px of pointer movement, read when a drag starts: a width in percent of a row needs the row */
  unitsPerPx?: (handle: HTMLElement) => number
  /** Decimals a dragged width keeps. @default 0 (whole px) */
  decimals?: number
  /** The width as a screen reader should say it, when the bare number is unclear */
  valueText?: string
}

/** px per arrow key press */
const defaultStep = 16
/** Less movement than this is a tap, not a drag */
const tapSlop = 3
/**
 * A tap's toggle waits out the double-click window, so a double-click resets instead of toggling twice first
 * (Joni's review of #3015, P1 2)
 */
const doubleClickWindow = 250

interface Drag {
  pointerId: number
  startX: number
  startWidth: number
  width: number
  /** Value units per px for this drag */
  scale: number
  moved: boolean
  cancelled: boolean
}

const pxScale = () => 1

/** The props that make an element a resize separator: role, ARIA, focus and the pointer and key handlers */
export const useResizableSeparator = (options: ResizableSeparatorOptions) => {
  const {
    value,
    min,
    max,
    defaultValue,
    label,
    controls,
    onResize,
    onCommit,
    onDragEnd,
    edge = 'left',
    step = defaultStep,
    unitsPerPx = pxScale,
    decimals = 0,
    valueText,
  } = options
  const drag = useRef<Drag | null>(null)
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Removes the drag's Esc listener */
  const stopListening = useRef<(() => void) | null>(null)
  const precision = 10 ** decimals
  const clamp = (width: number) => Math.round(Math.min(max, Math.max(min, width)) * precision) / precision
  /** Moving toward the panel's outside widens it: left for a panel on the right, right for one on the left */
  const widenDirection = edge === 'left' ? -1 : 1
  const widenKey = edge === 'left' ? 'ArrowLeft' : 'ArrowRight'
  const narrowKey = edge === 'left' ? 'ArrowRight' : 'ArrowLeft'

  useEffect(
    () => () => {
      if (tapTimer.current) {
        clearTimeout(tapTimer.current)
      }
      stopListening.current?.()
    },
    []
  )

  /** The drag ends without saving: the start width is put back */
  const cancelDrag = () => {
    const current = drag.current
    if (!current || current.cancelled) {
      return
    }
    current.cancelled = true
    onResize(current.startWidth)
  }

  const endDrag = () => {
    drag.current = null
    stopListening.current?.()
    stopListening.current = null
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    /**
     * Esc during a drag puts the start width back, wherever focus is: a mouse drag never focuses the handle, so Esc
     * would otherwise reach the panel and close it (Joni's review of #3015, P1 1). Caught first, and stopped there.
     */
    const handleEscape = (keyEvent: globalThis.KeyboardEvent) => {
      if (keyEvent.key !== 'Escape' || !drag.current || drag.current.cancelled) {
        return
      }
      keyEvent.preventDefault()
      keyEvent.stopPropagation()
      cancelDrag()
    }
    stopListening.current?.()
    window.addEventListener('keydown', handleEscape, true)
    stopListening.current = () => window.removeEventListener('keydown', handleEscape, true)
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: value,
      width: value,
      scale: unitsPerPx(event.currentTarget),
      moved: false,
      cancelled: false,
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (!current || current.cancelled || current.pointerId !== event.pointerId) {
      return
    }
    const dx = event.clientX - current.startX
    if (!current.moved && Math.abs(dx) < tapSlop) {
      return
    }
    current.moved = true
    current.width = clamp(current.startWidth + widenDirection * dx * current.scale)
    onResize(current.width)
  }

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    endDrag()
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    if (!current) {
      return
    }
    if (current.cancelled) {
      onDragEnd?.()
      return
    }
    if (current.moved) {
      onCommit(current.width)
      onDragEnd?.()
      return
    }
    /** No movement: a tap toggles, so a width can be chosen without dragging, once no double-click follows */
    /** Where the default is the max (a narrow row), the other width is the min, so a tap still changes something */
    const other = defaultValue === max ? min : max
    const toggled = value === defaultValue ? other : defaultValue
    if (tapTimer.current) {
      clearTimeout(tapTimer.current)
    }
    tapTimer.current = setTimeout(() => {
      tapTimer.current = null
      onCommit(toggled)
    }, doubleClickWindow)
  }

  /** The browser took the pointer back (a touch turned into a scroll, say): nothing is saved or toggled (P2 3) */
  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (current && !current.cancelled) {
      onResize(current.startWidth)
    }
    endDrag()
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    if (current) {
      onDragEnd?.()
    }
  }

  const handleDoubleClick = () => {
    if (tapTimer.current) {
      clearTimeout(tapTimer.current)
      tapTimer.current = null
    }
    onCommit(defaultValue)
  }

  /** The width a key asks for, or null for a key the separator doesn't use */
  const keyTarget = (key: string) => {
    switch (key) {
      case widenKey:
        return value + step
      case narrowKey:
        return value - step
      case 'Home':
        return min
      case 'End':
        return max
      default:
        return null
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = keyTarget(event.key)
    if (target === null) {
      return
    }
    event.preventDefault()
    onCommit(clamp(target))
  }

  return {
    role: 'separator',
    'aria-orientation': 'vertical',
    'aria-label': label,
    'aria-controls': controls,
    'aria-valuenow': value,
    'aria-valuemin': min,
    'aria-valuemax': max,
    'aria-valuetext': valueText,
    tabIndex: 0,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onKeyDown: handleKeyDown,
    onDoubleClick: handleDoubleClick,
  } as const
}
